// /invite/:code — where the "Log in to MARS" button in the invite email lands.
//
// Opening the page only looks the invite up (so a mail scanner that prefetches
// the link can't use it up). The person's email is filled in; pressing Log in
// spends the one-time code and signs them in — no typing. The temporary
// password is never in the link; it's in the email for when the link expires.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, AlertCircle, Users } from 'lucide-react';
import { inviteApi } from '../../services/account';
import { useTheme } from '../../context/ThemeContext';
import SetPasswordForm from './SetPasswordForm';

const Card = ({ children }) => {
  const { isDark } = useTheme();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 shadow-sm p-8">
        <img src={isDark ? '/MARS-White.png' : '/MARS-Color.png'} alt="MARS" className="h-9 mx-auto mb-6" />
        {children}
      </div>
    </div>
  );
};

const AcceptInvite = () => {
  const { code } = useParams();
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState('loading'); // loading | ready | signing | set-password | error
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    inviteApi.lookup(code)
      .then((data) => { if (alive) { setInvite(data); setStep('ready'); } })
      .catch((e) => { if (alive) { setError(e.message); setStep('error'); } });
    return () => { alive = false; };
  }, [code]);

  const goToApp = () => { window.location.href = '/dashboard'; };

  const login = async () => {
    setStep('signing');
    setError('');
    try {
      const res = await inviteApi.login(code);
      if (res.mustChangePassword) setStep('set-password');
      else goToApp();
    } catch (e) {
      setError(e.message);
      setStep('ready');
      if (e.code === 'INVITE_EXPIRED') setInvite((i) => ({ ...(i || {}), status: 'expired' }));
    }
  };

  const signInLink = `/login${invite?.email ? `?email=${encodeURIComponent(invite.email)}` : ''}`;

  if (step === 'loading') {
    return <Card><p className="text-center text-sm text-gray-500">Checking your invite…</p></Card>;
  }

  if (step === 'set-password') {
    return (
      <Card>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100 text-center">Choose your password</h1>
        <p className="mt-1 mb-6 text-sm text-gray-500 dark:text-slate-400 text-center">
          You're in. Pick a password you'll use from now on instead of the temporary one.
        </p>
        <SetPasswordForm onDone={goToApp} />
      </Card>
    );
  }

  if (step === 'error' || !invite || invite.status !== 'valid') {
    const status = invite?.status;
    const title = status === 'expired'
      ? 'This link has expired'
      : status === 'inactive'
        ? 'Your access is paused'
        : "This link can't be used";
    const body = status === 'expired'
      ? 'Sign in with the email and temporary password from your invite email, or ask your team admin to send a new invite.'
      : status === 'inactive'
        ? 'Your team admin has suspended this login. Please contact them.'
        : 'It has already been used, or it is not complete. Sign in with the email and password from your invite email.';
    return (
      <Card>
        <div className="text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">{error && step === 'error' ? error : body}</p>
          {status !== 'inactive' && (
            <a href={signInLink} className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 text-sm font-semibold">
              Go to sign in <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-3 py-1 text-xs font-medium">
          <Users className="w-3.5 h-3.5" /> {invite.accountName}
        </div>
        <h1 className="mt-3 text-xl font-semibold text-gray-900 dark:text-slate-100">
          {invite.firstName ? `Welcome, ${invite.firstName}` : 'Welcome to MARS'}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">You've been added to this team. Your details are filled in.</p>
      </div>
      <div className="space-y-3">
        <div className="relative">
          <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input readOnly value={invite.email} aria-label="Email"
            className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 pl-9 pr-3 py-2.5 text-sm text-gray-800 dark:text-slate-100" />
        </div>
        <div className="relative">
          <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input readOnly type="password" value="temporary-pass" aria-label="Password (from your invite)"
            className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 pl-9 pr-3 py-2.5 text-sm text-gray-800 dark:text-slate-100" />
        </div>
        <p className="text-xs text-gray-500 dark:text-slate-400">Your temporary password comes from the invite — no need to type it.</p>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button type="button" onClick={login} disabled={step === 'signing'}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white py-2.5 text-sm font-semibold disabled:opacity-60">
          {step === 'signing' ? 'Signing you in…' : <>Log in <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </Card>
  );
};

export default AcceptInvite;
